/**
 * Custom hooks for HR document management
 */

import { useState, useEffect, useCallback } from 'react'
import { 
  HRService, 
  HRDocument, 
  DocumentListParams, 
  UploadDocumentParams,
  DocumentStats 
} from '@/services/hrService'
import { ApiError } from '@/services/api'

// Hook return types
export interface UseHRDocumentsReturn {
  documents: HRDocument[]
  loading: boolean
  error: string | null
  pagination?: {
    totalItems: number
    currentPage: number
    itemsPerPage: number
    totalPages: number
  }
  refetch: () => Promise<void>
}

export interface UseDocumentUploadReturn {
  uploading: boolean
  uploadError: string | null
  uploadDocument: (params: UploadDocumentParams) => Promise<HRDocument | null>
  clearError: () => void
}

export interface UseDocumentStatsReturn {
  stats: DocumentStats | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export interface UseDocumentEditReturn {
  editing: boolean
  editError: string | null
  updateDocument: (id: string, updates: Partial<Pick<HRDocument, 'title' | 'description' | 'category'>>) => Promise<HRDocument | null>
  clearError: () => void
}

/**
 * Hook for fetching and managing HR documents list
 */
export const useHRDocuments = (params: DocumentListParams = {}) => {
  const [documents, setDocuments] = useState<HRDocument[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<UseHRDocumentsReturn['pagination']>()

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await HRService.getDocuments(params)
      setDocuments(response.documents)
      setPagination(response.pagination)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to fetch documents')
      console.error('Error fetching documents:', err)
    } finally {
      setLoading(false)
    }
  }, [params.category, params.search, params.status, params.page, params.limit, params.sortBy, params.sortOrder])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  return {
    documents,
    loading,
    error,
    pagination,
    refetch: fetchDocuments,
  }
}

/**
 * Hook for uploading documents
 */
export const useDocumentUpload = () => {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const uploadDocument = useCallback(async (params: UploadDocumentParams): Promise<HRDocument | null> => {
    try {
      setUploading(true)
      setUploadError(null)
      
      const response = await HRService.uploadDocument(params)
      return response.document
    } catch (err) {
      const apiError = err as ApiError
      setUploadError(apiError.message || 'Failed to upload document')
      console.error('Error uploading document:', err)
      return null
    } finally {
      setUploading(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setUploadError(null)
  }, [])

  return {
    uploading,
    uploadError,
    uploadDocument,
    clearError,
  }
}

/**
 * Hook for editing document metadata
 */
export const useDocumentEdit = () => {
  const [editing, setEditing] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  const updateDocument = useCallback(async (
    id: string, 
    updates: Partial<Pick<HRDocument, 'title' | 'description' | 'category'>>
  ): Promise<HRDocument | null> => {
    try {
      setEditing(true)
      setEditError(null)
      
      const response = await HRService.updateDocument(id, updates)
      return response.document
    } catch (err) {
      const apiError = err as ApiError
      setEditError(apiError.message || 'Failed to update document')
      console.error('Error updating document:', err)
      return null
    } finally {
      setEditing(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setEditError(null)
  }, [])

  return {
    editing,
    editError,
    updateDocument,
    clearError,
  }
}

/**
 * Hook for document statistics
 */
export const useDocumentStats = () => {
  const [stats, setStats] = useState<DocumentStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await HRService.getDocumentStats()
      setStats(response)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to fetch document statistics')
      console.error('Error fetching document stats:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  }
}

/**
 * Hook for individual document operations (view, download, delete)
 */
export const useDocumentActions = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const downloadDocument = useCallback(async (id: string, filename: string) => {
    try {
      setLoading(true)
      setError(null)
      
      await HRService.triggerDownload(id, filename)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to download document')
      console.error('Error downloading document:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const deleteDocument = useCallback(async (id: string) => {
    try {
      setLoading(true)
      setError(null)
      
      await HRService.deleteDocument(id)
      return true
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to delete document')
      console.error('Error deleting document:', err)
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  const getDocumentUrl = useCallback(async (id: string) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await HRService.getDocumentUrl(id)
      return response.url
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to get document URL')
      console.error('Error getting document URL:', err)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    loading,
    error,
    downloadDocument,
    deleteDocument,
    getDocumentUrl,
    clearError,
  }
} 